import * as React from 'react';
import { api } from '../platform/index.ts';
import type { BadgeKey } from '../platform/index.ts';
import { ALERTS_CHANGED_EVENT } from '../platform/index.ts';
import { useAuth } from './auth.tsx';

/** Dispatched on window by the Board page when a card changes (the sidebar count reloads). */
export const BOARD_CHANGED_EVENT = 'cortex:board-changed';

// The Alerts (red) and Board (neutral) sidebar counts. Alerts come from GET /api/alerts/badge
// (every 30 s, and at once when a page changes an alert); Board is the caller's open cards, from
// GET /api/board/badge on the same clock (and at once when the Board page changes a card).
// Tests pass `counts` to fix them.
export type BadgeCounts = Record<BadgeKey, number>;

const BadgesContext = React.createContext<BadgeCounts>({ alerts: 0, board: 0 });

const POLL_MS = 30_000;

function LiveBadges({ children }: { children?: React.ReactNode }) {
  const { state } = useAuth();
  const signedIn = state.status === 'signed-in';
  const [alerts, setAlerts] = React.useState(0);
  const [board, setBoard] = React.useState(0);

  React.useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    const load = async () => {
      try {
        const [a, b] = await Promise.allSettled([api.GET('/api/alerts/badge'), api.GET('/api/board/badge')]);
        if (alive && a.status === 'fulfilled' && a.value.data) setAlerts(a.value.data.count);
        if (alive && b.status === 'fulfilled' && b.value.data) setBoard(b.value.data.open);
      } catch {
        // The badge is a hint; an unreachable API leaves the last count.
      }
    };
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    const onChange = () => void load();
    window.addEventListener(ALERTS_CHANGED_EVENT, onChange);
    window.addEventListener(BOARD_CHANGED_EVENT, onChange);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener(ALERTS_CHANGED_EVENT, onChange);
      window.removeEventListener(BOARD_CHANGED_EVENT, onChange);
    };
  }, [signedIn]);

  const value = React.useMemo(
    () => ({ alerts: signedIn ? alerts : 0, board: signedIn ? board : 0 }),
    [alerts, board, signedIn],
  );
  return <BadgesContext.Provider value={value}>{children}</BadgesContext.Provider>;
}

export function BadgesProvider({ counts, children }: { counts?: BadgeCounts; children?: React.ReactNode }) {
  if (counts) return <BadgesContext.Provider value={counts}>{children}</BadgesContext.Provider>;
  return <LiveBadges>{children}</LiveBadges>;
}

export const useBadges = () => React.useContext(BadgesContext);

/** How a count reads on a badge: hidden at 0, capped at 99+. */
export const badgeLabel = (n: number): string | null => (n > 0 ? (n > 99 ? '99+' : String(n)) : null);
